import React, { useEffect, useState } from 'react'
import { InputField, Modal, Select } from '@enamelai/design-system'
import { CANCEL_MEMBERSHIP_REASONS } from '@constants/Membership/membershipPage.constants'
import { useNotificationToast } from '@context/NotificationToastContext'
import type { MembershipPageMember } from '@dataTypes/membership.type'
import { updateMembershipMemberStatus } from '@store/actions/membership.action'
import { useAppDispatch } from '@store/hooks'
import {
  createFailedToast,
  createMembershipCanceledToast,
} from '@utils/notificationToast.util'
import './CancelMembershipModal.css'

type CancelMembershipModalProps = {
  open: boolean
  member?: MembershipPageMember | null
  onClose: () => void
  onCanceled?: (member: MembershipPageMember) => void
}

const CancelMembershipModal: React.FC<CancelMembershipModalProps> = ({
  open,
  member,
  onClose,
  onCanceled,
}) => {
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const [stage, setStage] = useState<'form' | 'confirm'>('form')
  const [reason, setReason] = useState('')
  const [otherReason, setOtherReason] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) return

    setStage('form')
    setReason('')
    setOtherReason('')
    setSaving(false)
  }, [open])

  const memberName = member?.name ?? 'Patient'

  const handleClose = (): void => {
    setStage('form')
    onClose()
  }

  const handleProceedFromForm = (): void => {
    setStage('confirm')
  }

  const handleCancelConfirm = (): void => {
    setStage('form')
  }

  const handleConfirmCancel = (): void => {
    if (!member || saving) return

    const statusReason = [reason.trim(), otherReason.trim()]
      .filter(Boolean)
      .join(': ')

    setSaving(true)
    void dispatch(
      updateMembershipMemberStatus({
        id: member.id,
        status: 'inactive',
        ...(statusReason ? { reason: statusReason } : {}),
      }),
    ).then((result) => {
      setSaving(false)

      if (updateMembershipMemberStatus.rejected.match(result)) {
        showToast(
          createFailedToast(
            'Unable to cancel membership',
            result.payload || 'Failed to cancel membership.',
          ),
        )
        return
      }

      showToast(createMembershipCanceledToast(memberName))
      onCanceled?.(member)
      handleClose()
    })
  }

  const handleReasonChange = (value: string | string[]): void => {
    setReason(typeof value === 'string' ? value : (value[0] ?? ''))
  }

  return (
    <>
      <Modal
        open={open && stage === 'form'}
        onClose={handleClose}
        size="md"
        title={`Cancel membership: ${memberName}`}
        className="cancel-membership-modal"
        bodyClassName="cancel-membership-modal__body"
        primaryLabel="Proceed"
        secondaryLabel="Cancel"
        onPrimaryClick={handleProceedFromForm}
        onSecondaryClick={handleClose}
      >
        <Select
          className="cancel-membership-modal__select"
          label="Reason for canceling"
          placeholder="Select here"
          options={CANCEL_MEMBERSHIP_REASONS}
          value={reason}
          onChange={handleReasonChange}
        />
        <InputField
          className="cancel-membership-modal__notes"
          label="Others reasons"
          variant="message"
          placeholder="Write here..."
          rows={3}
          maxLength={100}
          showCounter
          value={otherReason}
          onChange={(event) => setOtherReason(event.target.value)}
        />
      </Modal>
      <Modal
        open={open && stage === 'confirm'}
        onClose={handleCancelConfirm}
        size="sm"
        title="Cancel Membership"
        className="cancel-membership-modal--confirm"
        bodyClassName="cancel-membership-modal__confirm-body"
        primaryLabel="Proceed"
        primaryDestructive
        primaryDisabled={saving}
        secondaryLabel="Cancel"
        onPrimaryClick={handleConfirmCancel}
        onSecondaryClick={handleCancelConfirm}
      >
        <p className="cancel-membership-modal__copy">
          Are you sure you want to cancel {memberName}
          {'\u2019'}s plan?
          <br />
          <br />
          Their membership plan will remain active throughout current billing
          cycle and will not renew afterward.
        </p>
      </Modal>
    </>
  )
}

export default CancelMembershipModal
